/**
 * Meetings Page - Video/Voice calls with invitations
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MeetingsPage.css';
import PageHeader from '../../components/shared/PageHeader';
import Button from '../../components/shared/Button';
import Badge from '../../components/shared/Badge';
import Card from '../../components/shared/Card';
import { useToast } from '../../context/ToastContext';
import { useWebRTC } from '../../hooks/useWebRTC';
import { API_BASE, getPublicMeetingUrl } from '../../config/api';

interface Meeting {
  id: string;
  room_code: string;
  title: string;
  description?: string;
  meeting_type: 'video' | 'voice';
  status: 'scheduled' | 'live' | 'ended';
  scheduled_start?: number;
  host_name?: string;
  host_id?: string;
}

interface ChatMessage {
  id: string;
  userId: string;
  userName: string;
  message: string;
  timestamp: number;
}

interface Participant {
  id: string;
  odUserId: string;
  name: string;
  avatar?: string;
  isMuted: boolean;
  hasVideo: boolean;
  role: string;
}

// Generate a temporary user ID for demo
const getUserId = () => {
  let id = localStorage.getItem('demo_user_id');
  if (!id) {
    id = 'user_' + Math.random().toString(36).substring(2, 10);
    localStorage.setItem('demo_user_id', id);
  }
  return id;
};

const getUserName = () => {
  return localStorage.getItem('demo_user_name') || 'Guest User';
};

export default function MeetingsPage() {
  const [view, setView] = useState<'lobby' | 'meeting'>('lobby');
  const [roomCode, setRoomCode] = useState('');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [meetingTitle, setMeetingTitle] = useState('');
  const [meetingType, setMeetingType] = useState<'video' | 'voice'>('video');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [pinnedUserId, setPinnedUserId] = useState<string | null>(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [lastChatTime, setLastChatTime] = useState(0);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const { addToast } = useToast();

  const userId = getUserId();
  const userName = getUserName();

  // Check if current user is admin/host
  const isAdmin = meeting?.host_id === userId;

  const {
    localStream,
    screenStream,
    remoteStreams,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    connectionState,
    startMedia,
    stopMedia,
    connectToPeer,
    toggleAudio,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    startSignalPolling,
    disconnectAll,
  } = useWebRTC(
    roomCode,
    userId,
    // onRemoteStream
    (odUserId, stream) => {
      console.log('[Meeting] Remote stream from', odUserId);
    },
    // onPeerDisconnect
    (odUserId) => {
      setParticipants(prev => prev.filter(p => p.odUserId !== odUserId));
    }
  );

  // Attach local stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  // Create a new meeting
  const createMeeting = async () => {
    try {
      const scheduledStart = scheduledDate && scheduledTime
        ? Math.floor(new Date(`${scheduledDate}T${scheduledTime}`).getTime() / 1000)
        : null;

      const response = await fetch(`${API_BASE}/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: meetingTitle || 'Video Meeting',
          hostId: userId,
          meetingType,
          scheduledStart,
        }),
      });

      const data = await response.json();
      setRoomCode(data.roomCode);
      addToast('success', `Meeting created! Room: ${data.roomCode}`);

      // Copy to clipboard
      navigator.clipboard.writeText(data.roomCode);

      // If scheduled, show invite modal
      if (scheduledStart) {
        setShowInviteModal(true);
      } else {
        // Join immediately
        await joinMeeting(data.roomCode);
      }
    } catch (err) {
      console.error('Failed to create meeting:', err);
      addToast('error', 'Failed to create meeting');
    }
  };

  // Join an existing meeting
  const joinMeeting = async (code?: string) => {
    const joinCode = code || roomCode;
    if (!joinCode.trim()) {
      addToast('warning', 'Please enter a room code');
      return;
    }

    try {
      // Get meeting info
      const meetingRes = await fetch(`${API_BASE}/meetings/${joinCode}`);
      if (!meetingRes.ok) {
        addToast('error', 'Meeting not found');
        return;
      }
      const meetingData = await meetingRes.json();
      setMeeting(meetingData);

      // Join the meeting
      const joinRes = await fetch(`${API_BASE}/meetings/${joinCode}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          guestName: userName,
        }),
      });
      const joinData = await joinRes.json();

      // Try to start media (but don't fail if no camera/mic)
      try {
        await startMedia(meetingData.meeting_type);
      } catch (mediaErr) {
        console.warn('[Meet] Could not access camera/mic:', mediaErr);
        addToast('warning', 'No camera/mic found - joining as viewer');
      }

      // Start polling for signals
      startSignalPolling();

      // Get existing participants and connect to them
      const participantsRes = await fetch(`${API_BASE}/meetings/${joinCode}/participants`);
      const participantsData = await participantsRes.json();
      setParticipants(participantsData.participants);

      // Connect to all existing participants
      for (const p of participantsData.participants) {
        if (p.odUserId !== userId) {
          await connectToPeer(p.odUserId);
        }
      }

      setRoomCode(joinCode);
      setView('meeting');
      addToast('success', 'Joined meeting!');
    } catch (err) {
      console.error('Failed to join meeting:', err);
      addToast('error', 'Failed to join meeting');
    }
  };

  // Leave the meeting
  const leaveMeeting = async () => {
    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });

      disconnectAll();
      setView('lobby');
      setMeeting(null);
      setParticipants([]);
      addToast('info', 'Left the meeting');
    } catch (err) {
      console.error('Failed to leave meeting:', err);
    }
  };

  // End the meeting (host only)
  const endMeeting = async () => {
    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostId: userId }),
      });

      disconnectAll();
      setView('lobby');
      setMeeting(null);
      addToast('info', 'Meeting ended');
    } catch (err) {
      console.error('Failed to end meeting:', err);
      addToast('error', 'Only the host can end the meeting');
    }
  };

  // Send invites via email
  const sendInvites = async () => {
    if (!inviteEmails.trim()) {
      addToast('warning', 'Please enter email addresses');
      return;
    }

    const emails = inviteEmails.split(/[,\n]/).map(e => e.trim()).filter(Boolean);

    try {
      const response = await fetch(`${API_BASE}/meetings/${roomCode}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emails,
          sendCalendar: true,
        }),
      });

      const data = await response.json();
      addToast('success', `Invites sent to ${emails.length} recipients`);
      setShowInviteModal(false);
      setInviteEmails('');
    } catch (err) {
      console.error('Failed to send invites:', err);
      addToast('error', 'Failed to send invites');
    }
  };

  // Download calendar file
  const downloadCalendar = () => {
    if (!roomCode) return;
    window.open(`${API_BASE}/meetings/${roomCode}/calendar`, '_blank');
  };

  // Add to calendar via mailto
  const addToCalendar = () => {
    if (!meeting) return;
    const joinUrl = `${window.location.origin}/meet/${roomCode}`;
    const startDate = meeting.scheduled_start
      ? new Date(meeting.scheduled_start * 1000).toISOString()
      : new Date().toISOString();

    // Google Calendar link
    const googleUrl = new URL('https://calendar.google.com/calendar/render');
    googleUrl.searchParams.set('action', 'TEMPLATE');
    googleUrl.searchParams.set('text', meeting.title || 'Video Meeting');
    googleUrl.searchParams.set('details', `Join: ${joinUrl}`);
    googleUrl.searchParams.set('dates', startDate.replace(/[-:]/g, '').replace(/\.\d{3}/, '') + '/' +
      new Date(new Date(startDate).getTime() + 3600000).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, ''));

    window.open(googleUrl.toString(), '_blank');
  };

  // Kick participant (admin only)
  const kickParticipant = async (participantId: string) => {
    if (!isAdmin) return;

    try {
      await fetch(`${API_BASE}/meetings/${roomCode}/kick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostId: userId, participantId }),
      });
      setParticipants(prev => prev.filter(p => p.odUserId !== participantId));
      addToast('info', 'Participant removed');
    } catch (err) {
      console.error('Failed to kick participant:', err);
      addToast('error', 'Failed to remove participant');
    }
  };

  // Send chat message via API
  const sendChatMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    try {
      const res = await fetch(`${API_BASE}/meetings/${roomCode}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          userName,
          message: messageInput,
        }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, data]);
      setMessageInput('');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  // Poll for chat messages
  useEffect(() => {
    if (view !== 'meeting' || !roomCode) return;

    const pollChat = async () => {
      try {
        const res = await fetch(`${API_BASE}/meetings/${roomCode}/chat?since=${lastChatTime}`);
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setChatMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = data.messages.filter((m: ChatMessage) => !existingIds.has(m.id));
            return [...prev, ...newMsgs];
          });
          setLastChatTime(Math.max(...data.messages.map((m: ChatMessage) => m.timestamp)));
        }
      } catch (err) {
        console.error('Chat poll error:', err);
      }
    };

    pollChat();
    const interval = setInterval(pollChat, 2000);
    return () => clearInterval(interval);
  }, [view, roomCode, lastChatTime]);

  // Pin/unpin video
  const togglePinVideo = (odUserId: string) => {
    setPinnedUserId(prev => prev === odUserId ? null : odUserId);
  };

  // Copy room link
  const copyRoomLink = () => {
    const link = getPublicMeetingUrl(roomCode);
    navigator.clipboard.writeText(link);
    addToast('success', 'Link copied to clipboard!');
  };

  // ============================================
  // RENDER: LOBBY VIEW
  // ============================================
  if (view === 'lobby') {
    return (
      <div className="meetings-page">
        <PageHeader
          title="Video Meetings"
          breadcrumb={['Studio', 'Meetings']}
        />

        <div className="meetings-lobby">
          <div className="meetings-lobby__grid">
            {/* Create Meeting Card */}
            <Card className="meeting-card meeting-card--create">
              <div className="meeting-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2"/>
                </svg>
              </div>
              <h2>Start a Meeting</h2>
              <p>Create a new video or voice meeting</p>

              <div className="meeting-form">
                <input
                  type="text"
                  placeholder="Meeting title (optional)"
                  value={meetingTitle}
                  onChange={e => setMeetingTitle(e.target.value)}
                  className="meeting-input"
                />

                <div className="meeting-type-toggle">
                  <button
                    className={`type-btn ${meetingType === 'video' ? 'active' : ''}`}
                    onClick={() => setMeetingType('video')}
                  >
                    📹 Video
                  </button>
                  <button
                    className={`type-btn ${meetingType === 'voice' ? 'active' : ''}`}
                    onClick={() => setMeetingType('voice')}
                  >
                    🎙️ Voice
                  </button>
                </div>

                <div className="meeting-schedule">
                  <label className="schedule-label">
                    <input
                      type="checkbox"
                      checked={!!scheduledDate}
                      onChange={e => {
                        if (!e.target.checked) {
                          setScheduledDate('');
                          setScheduledTime('');
                        } else {
                          setScheduledDate(new Date().toISOString().split('T')[0]);
                          setScheduledTime('10:00');
                        }
                      }}
                    />
                    Schedule for later
                  </label>

                  {scheduledDate && (
                    <div className="schedule-inputs">
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={e => setScheduledDate(e.target.value)}
                      />
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={e => setScheduledTime(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <Button onClick={createMeeting} style={{ width: '100%' }}>
                  {scheduledDate ? 'Schedule Meeting' : 'Start Now'}
                </Button>
              </div>
            </Card>

            {/* Join Meeting Card */}
            <Card className="meeting-card meeting-card--join">
              <div className="meeting-card__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </div>
              <h2>Join a Meeting</h2>
              <p>Enter the room code to join</p>

              <div className="meeting-form">
                <input
                  type="text"
                  placeholder="Enter room code (e.g., ABC-123-XYZ)"
                  value={roomCode}
                  onChange={e => setRoomCode(e.target.value.toUpperCase())}
                  className="meeting-input meeting-input--code"
                />

                <Button onClick={() => joinMeeting()} style={{ width: '100%' }}>
                  Join Meeting
                </Button>
              </div>
            </Card>
          </div>

          {/* Features */}
          <div className="meeting-features">
            <div className="feature">
              <span className="feature__icon">🔒</span>
              <span>End-to-End Encrypted</span>
            </div>
            <div className="feature">
              <span className="feature__icon">📧</span>
              <span>Email Invitations</span>
            </div>
            <div className="feature">
              <span className="feature__icon">📅</span>
              <span>Calendar Integration</span>
            </div>
            <div className="feature">
              <span className="feature__icon">🖥️</span>
              <span>Screen Sharing</span>
            </div>
          </div>
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
            <div className="modal invite-modal" onClick={e => e.stopPropagation()}>
              <div className="modal__header">
                <h3>Invite Participants</h3>
                <button className="modal__close" onClick={() => setShowInviteModal(false)}>×</button>
              </div>

              <div className="modal__body">
                <div className="invite-room-info">
                  <span className="invite-room-label">Room Code:</span>
                  <span className="invite-room-code">{roomCode}</span>
                  <button className="copy-btn" onClick={copyRoomLink}>Copy Link</button>
                </div>

                <div className="invite-field">
                  <label>Email Addresses</label>
                  <textarea
                    placeholder="Enter email addresses (one per line or comma-separated)"
                    value={inviteEmails}
                    onChange={e => setInviteEmails(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="invite-actions">
                  <Button onClick={sendInvites}>
                    📧 Send Email Invites
                  </Button>
                  <Button variant="ghost" onClick={downloadCalendar}>
                    📅 Download .ics
                  </Button>
                  <Button variant="ghost" onClick={addToCalendar}>
                    📆 Add to Google Calendar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // RENDER: MEETING VIEW
  // ============================================
  return (
    <div className="meetings-page meetings-page--in-call">
      {/* Meeting Header */}
      <div className="meeting-header">
        <div className="meeting-header__info">
          <h2>{meeting?.title || 'Video Meeting'}</h2>
          <Badge variant={meeting?.status === 'live' ? 'success' : 'default'}>
            {meeting?.status === 'live' ? 'Live' : meeting?.status}
          </Badge>
          <span className="meeting-code">{roomCode}</span>
        </div>
        <div className="meeting-header__actions">
          <button className="header-btn" onClick={() => setShowInviteModal(true)} title="Invite">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
          </button>
          <button
            className={`header-btn ${showParticipants ? 'active' : ''}`}
            onClick={() => setShowParticipants(!showParticipants)}
            title="Participants"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span className="header-btn__badge">{participants.length}</span>
          </button>
          <button className="header-btn" onClick={copyRoomLink} title="Copy Link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
          <button
            className={`header-btn ${showChat ? 'active' : ''}`}
            onClick={() => setShowChat(!showChat)}
            title="Chat"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          {isAdmin && (
            <button className="header-btn header-btn--danger" onClick={endMeeting} title="End Meeting">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Video Grid */}
      <div className={`meeting-content ${showChat ? 'meeting-content--with-chat' : ''} ${showParticipants ? 'meeting-content--with-participants' : ''}`}>
        {/* Pinned Video (if any) */}
        {pinnedUserId && (
          <div className="video-pinned" onClick={() => setPinnedUserId(null)}>
            {pinnedUserId === userId ? (
              isVideoEnabled ? (
                <video ref={localVideoRef} autoPlay muted playsInline className="video-pinned__video" />
              ) : (
                <div className="video-pinned__placeholder">
                  <div className="video-pinned__avatar">{userName[0]?.toUpperCase()}</div>
                </div>
              )
            ) : (
              <video
                autoPlay
                playsInline
                className="video-pinned__video"
                ref={el => {
                  const stream = remoteStreams.get(pinnedUserId);
                  if (el && stream && el.srcObject !== stream) {
                    el.srcObject = stream;
                  }
                }}
              />
            )}
            <div className="video-pinned__label">
              📌 {pinnedUserId === userId ? 'You' : participants.find(p => p.odUserId === pinnedUserId)?.name || 'Guest'}
              <span className="video-pinned__hint">Click to unpin</span>
            </div>
          </div>
        )}

        <div className={`video-grid ${pinnedUserId ? 'video-grid--with-pinned' : ''}`}>
          {/* Local video */}
          {pinnedUserId !== userId && (
          <div
            className="video-tile video-tile--local"
            onClick={() => togglePinVideo(userId)}
          >
            {isVideoEnabled && localStream ? (
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="video-tile__video"
              />
            ) : (
              <div className="video-tile__placeholder">
                <div className="video-tile__avatar">{userName[0]?.toUpperCase()}</div>
              </div>
            )}
            <div className="video-tile__name">
              {!isAudioEnabled && <span className="muted-icon">🔇</span>}
              You {isAdmin && <span className="admin-badge">👑</span>}
            </div>
            <div className="video-tile__actions">
              <button className="tile-action" title="Pin video">📌</button>
            </div>
          </div>
          )}

          {/* Remote videos */}
          {Array.from(remoteStreams.entries()).map(([odUserId, stream]) => {
            const participant = participants.find(p => p.odUserId === odUserId);
            if (pinnedUserId === odUserId) return null; // Skip if pinned
            return (
              <div
                key={odUserId}
                className="video-tile"
                onClick={() => togglePinVideo(odUserId)}
              >
                <video
                  autoPlay
                  playsInline
                  className="video-tile__video"
                  ref={el => {
                    if (el && el.srcObject !== stream) {
                      el.srcObject = stream;
                    }
                  }}
                />
                <div className="video-tile__name">
                  {participant?.isMuted && <span className="muted-icon">🔇</span>}
                  {participant?.name || 'Guest'}
                  {participant?.role === 'host' && <span className="admin-badge">👑</span>}
                </div>
                <div className="video-tile__actions">
                  <button className="tile-action" title="Pin video">📌</button>
                  {isAdmin && participant?.odUserId !== userId && (
                    <button
                      className="tile-action tile-action--danger"
                      onClick={(e) => { e.stopPropagation(); kickParticipant(odUserId); }}
                      title="Remove participant"
                    >
                      ❌
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Placeholder tiles for participants without streams yet */}
          {participants
            .filter(p => p.odUserId !== userId && !remoteStreams.has(p.odUserId) && p.odUserId !== pinnedUserId)
            .map(p => (
              <div key={p.odUserId} className="video-tile video-tile--connecting">
                <div className="video-tile__placeholder">
                  <div className="video-tile__avatar">{p.name[0]?.toUpperCase()}</div>
                  <div className="video-tile__connecting">Connecting...</div>
                </div>
                <div className="video-tile__name">{p.name}</div>
                {isAdmin && (
                  <div className="video-tile__actions">
                    <button
                      className="tile-action tile-action--danger"
                      onClick={() => kickParticipant(p.odUserId)}
                      title="Remove participant"
                    >
                      ❌
                    </button>
                  </div>
                )}
              </div>
            ))}
        </div>

        {/* Participants Panel */}
        {showParticipants && (
          <div className="participants-panel">
            <div className="participants-panel__header">
              <span>Participants ({participants.length})</span>
              <button onClick={() => setShowParticipants(false)}>×</button>
            </div>
            <div className="participants-panel__list">
              {participants.map(p => (
                <div key={p.odUserId} className="participant-item">
                  <div className="participant-item__avatar">{p.name[0]?.toUpperCase()}</div>
                  <div className="participant-item__info">
                    <span className="participant-item__name">
                      {p.name} {p.odUserId === userId && '(You)'}
                      {p.role === 'host' && <span className="admin-badge">👑</span>}
                    </span>
                    <span className="participant-item__status">
                      {p.isMuted ? '🔇 Muted' : '🎤 Speaking'}
                    </span>
                  </div>
                  {isAdmin && p.odUserId !== userId && (
                    <button
                      className="participant-item__kick"
                      onClick={() => kickParticipant(p.odUserId)}
                      title="Remove"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat Panel */}
        {showChat && (
          <div className="chat-panel">
            <div className="chat-panel__header">
              <span>Meeting Chat</span>
              <button onClick={() => setShowChat(false)}>×</button>
            </div>
            <div className="chat-panel__messages">
              {chatMessages.length === 0 ? (
                <div className="chat-empty">No messages yet</div>
              ) : (
                chatMessages.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.userId === userId ? 'chat-message--own' : ''}`}>
                    <div className="chat-message__sender">
                      {msg.userId === userId ? 'You' : msg.userName}
                      <span className="chat-message__time">
                        {new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="chat-message__text">{msg.message}</div>
                  </div>
                ))
              )}
            </div>
            <form className="chat-panel__input" onSubmit={sendChatMessage}>
              <input
                type="text"
                placeholder="Type a message..."
                value={messageInput}
                onChange={e => setMessageInput(e.target.value)}
              />
              <button type="submit">Send</button>
            </form>
          </div>
        )}
      </div>

      {/* Controls Bar */}
      <div className="meeting-controls">
        <button
          className={`control-btn ${!isAudioEnabled ? 'control-btn--off' : ''}`}
          onClick={toggleAudio}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
              <line x1="1" y1="1" x2="23" y2="23"/>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
              <line x1="12" y1="19" x2="12" y2="23"/>
              <line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
          )}
        </button>

        {meeting?.meeting_type === 'video' && (
          <button
            className={`control-btn ${!isVideoEnabled ? 'control-btn--off' : ''}`}
            onClick={toggleVideo}
            title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            {isVideoEnabled ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <polygon points="23 7 16 12 23 17 23 7"/>
                <rect x="1" y="5" width="15" height="14" rx="2"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
            )}
          </button>
        )}

        <button
          className={`control-btn ${isScreenSharing ? 'control-btn--active' : ''}`}
          onClick={isScreenSharing ? stopScreenShare : startScreenShare}
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
            <rect x="2" y="3" width="20" height="14" rx="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
        </button>

        <button
          className="control-btn control-btn--end"
          onClick={leaveMeeting}
          title="Leave meeting"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
            <line x1="23" y1="1" x2="1" y2="23"/>
          </svg>
        </button>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal invite-modal" onClick={e => e.stopPropagation()}>
            <div className="modal__header">
              <h3>Invite Participants</h3>
              <button className="modal__close" onClick={() => setShowInviteModal(false)}>×</button>
            </div>

            <div className="modal__body">
              <div className="invite-room-info">
                <span className="invite-room-label">Room Code:</span>
                <span className="invite-room-code">{roomCode}</span>
                <button className="copy-btn" onClick={copyRoomLink}>Copy Link</button>
              </div>

              <div className="invite-field">
                <label>Email Addresses</label>
                <textarea
                  placeholder="Enter email addresses (one per line or comma-separated)"
                  value={inviteEmails}
                  onChange={e => setInviteEmails(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="invite-actions">
                <Button onClick={sendInvites}>
                  📧 Send Email Invites
                </Button>
                <Button variant="ghost" onClick={downloadCalendar}>
                  📅 Download .ics
                </Button>
                <Button variant="ghost" onClick={addToCalendar}>
                  📆 Add to Google Calendar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
